import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  FlaskConical, RefreshCw, UserPlus, CheckCircle2, AlertTriangle,
  Building2, ClipboardList, Bell, FileText, Shield, Copy, Eye, EyeOff,
  ChevronRight, Loader2, Info
} from 'lucide-react';

interface SandboxStatus {
  cluster_id: string;
  hub_id: string;
  inspecoes_teste: number;
  alertas: number;
  emp_contratos: number;
  last_reset_at: string | null;
  last_seed_at: string | null;
  reset_count: number;
}

const SANDBOX_EMP_CONTRACT_IDS = [
  'c0000000-0000-0000-0000-000000000001',
  'c0000000-0000-0000-0000-000000000002',
];

const MODULE_MAP = [
  { label: 'Empreendimentos & Contratos', icon: Building2, path: '/empreendimentos/contratos', tip: 'Listagem dos 2 contratos sandbox: Residencial Alpha e Infraestrutura Beta.' },
  { label: 'BIM 3D (Visualização)', icon: FileText, path: `/empreendimentos/contratos/${SANDBOX_EMP_CONTRACT_IDS[0]}/bim`, tip: 'Use o modelo demo ou faça upload de um arquivo .ifc de teste.' },
  { label: 'Monte Carlo', icon: FlaskConical, path: `/empreendimentos/contratos/${SANDBOX_EMP_CONTRACT_IDS[0]}/montecarlo`, tip: 'Simule riscos de prazo e custo no contrato sandbox.' },
  { label: 'Cronograma & EVM', icon: ClipboardList, path: `/empreendimentos/contratos/${SANDBOX_EMP_CONTRACT_IDS[0]}/cronograma`, tip: 'Importe um XLSX de cronograma e analise indicadores EVM.' },
  { label: 'Central de Alertas', icon: Bell, path: '/empreendimentos', tip: 'Veja os 2 alertas sandbox: atraso crítico e prazo em risco.' },
  { label: 'Governança & Relatórios', icon: Shield, path: '/empreendimentos', tip: 'Acesse relatórios e governance dentro dos contratos sandbox.' },
];

export default function SandboxPage({ embedded = false }: { embedded?: boolean }) {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<SandboxStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingReset, setLoadingReset] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [sandboxUser, setSandboxUser] = useState<{ email: string; password: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data, error } = await supabase.functions.invoke('sandbox-manager', {
        body: { action: 'status' },
      });
      if (error) throw error;
      if (data?.ok) setStatus(data.sandbox);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erro ao carregar status sandbox', description: err.message });
    } finally {
      setLoadingStatus(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleReset = async () => {
    setLoadingReset(true);
    try {
      const { data, error } = await supabase.functions.invoke('sandbox-manager', {
        body: { action: 'reset' },
      });
      if (error) throw error;
      toast({
        title: '✅ Sandbox resetado!',
        description: `${data.steps?.length ?? 0} operações executadas. Dados restaurados ao estado inicial.`,
      });
      fetchStatus();
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erro ao resetar sandbox', description: err.message });
    } finally {
      setLoadingReset(false);
    }
  };

  const handleCreateUser = async () => {
    setLoadingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke('sandbox-manager', {
        body: { action: 'create_sandbox_user' },
      });
      if (error) throw error;
      setSandboxUser({ email: data.email, password: data.password });
      toast({ title: '✅ Usuário sandbox pronto!', description: data.message });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erro ao criar usuário', description: err.message });
    } finally {
      setLoadingUser(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiado!', description: `${label} copiado para a área de transferência.` });
  };

  if (!isAdmin) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Alert variant="destructive" className="max-w-md">
          <Shield className="h-4 w-4" />
          <AlertTitle>Acesso Restrito</AlertTitle>
          <AlertDescription>Apenas administradores podem acessar o painel de sandbox.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — hidden when embedded as tab in Ferramentas Dev */}
      {!embedded && (
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <FlaskConical className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sandbox de Testes</h1>
          <p className="text-muted-foreground text-sm">
            Cluster isolado para testes completos — zero risco para dados de produção
          </p>
        </div>
        <Badge variant="outline" className="ml-auto border-primary text-primary">
          🧪 SANDBOX
        </Badge>
      </div>
      )}

      {/* Warning */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Ambiente de Testes Isolado</AlertTitle>
        <AlertDescription>
          Todos os dados criados aqui estão no <strong>CLUSTER SANDBOX</strong> — completamente separado da produção.
          Use o botão "Resetar" para restaurar o estado inicial após cada sessão de testes.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Card */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Status do Ambiente</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchStatus} disabled={loadingStatus}>
              <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
              </div>
            ) : status ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-muted/40 rounded-lg">
                    <div className="text-2xl font-bold text-foreground">{status.emp_contratos}</div>
                    <div className="text-xs text-muted-foreground mt-1">Contratos</div>
                  </div>
                  <div className="text-center p-3 bg-muted/40 rounded-lg">
                    <div className="text-2xl font-bold text-foreground">{status.inspecoes_teste}</div>
                    <div className="text-xs text-muted-foreground mt-1">Inspeções</div>
                  </div>
                  <div className="text-center p-3 bg-muted/40 rounded-lg">
                    <div className="text-2xl font-bold text-foreground">{status.alertas}</div>
                    <div className="text-xs text-muted-foreground mt-1">Alertas</div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Resets realizados:</span>
                    <span className="font-medium">{status.reset_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Último reset:</span>
                    <span className="font-medium">
                      {status.last_reset_at
                        ? new Date(status.last_reset_at).toLocaleString('pt-BR')
                        : 'Nunca resetado'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Seed inicial:</span>
                    <span className="font-medium">
                      {status.last_seed_at
                        ? new Date(status.last_seed_at).toLocaleString('pt-BR')
                        : '—'}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleReset}
                  disabled={loadingReset}
                >
                  {loadingReset ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Resetar Dados de Teste
                </Button>
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">Não foi possível carregar o status.</div>
            )}
          </CardContent>
        </Card>

        {/* User Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Usuário de Teste</CardTitle>
            <CardDescription className="text-xs">Acesso admin completo ao sandbox</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sandboxUser ? (
              <div className="space-y-3">
                <Alert className="p-3">
                  <CheckCircle2 className="h-3 w-3" />
                  <AlertDescription className="text-xs ml-1">
                    Usuário pronto para login
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Email</label>
                    <div className="flex gap-1">
                      <code className="flex-1 text-xs bg-muted px-2 py-1.5 rounded font-mono truncate">
                        {sandboxUser.email}
                      </code>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(sandboxUser.email, 'Email')}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Senha</label>
                    <div className="flex gap-1">
                      <code className="flex-1 text-xs bg-muted px-2 py-1.5 rounded font-mono">
                        {showPassword ? sandboxUser.password : '••••••••••••'}
                      </code>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowPassword(v => !v)}>
                        {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(sandboxUser.password, 'Senha')}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Crie o usuário <strong>sandbox@siafo.test</strong> com role admin para testar todos os módulos sem usar sua conta pessoal.
                </p>
                <Button
                  className="w-full"
                  onClick={handleCreateUser}
                  disabled={loadingUser}
                >
                  {loadingUser ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  Criar Usuário Sandbox
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modules Guide */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            Guia de Módulos Testáveis
          </CardTitle>
          <CardDescription>
            Clique em qualquer módulo para navegar diretamente ao ambiente sandbox correspondente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MODULE_MAP.map((mod) => {
              const Icon = mod.icon;
              return (
                <a
                  key={mod.label}
                  href={mod.path}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors group cursor-pointer"
                >
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">{mod.label}</div>
                    <div className="text-xs text-muted-foreground truncate">{mod.tip}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Isolation Info */}
      <Card className="border-dashed">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-1">
                <Shield className="h-3.5 w-3.5 text-primary" /> Isolamento Garantido
              </div>
              <p className="text-xs text-muted-foreground">
                Dados sandbox estão no <strong>CLUSTER SANDBOX</strong> (ID prefixo a0000000).
                Views de produção filtram <code>is_test = true</code> automaticamente.
              </p>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-1">
                <RefreshCw className="h-3.5 w-3.5 text-primary" /> Reset Rápido
              </div>
              <p className="text-xs text-muted-foreground">
                O botão "Resetar" limpa medições, cronogramas, diário, efetivo, aditivos, penalidades,
                glosas e fotos de teste — restaurando o estado inicial em segundos.
              </p>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Limitações
              </div>
              <p className="text-xs text-muted-foreground">
                Geolocalização GPS retorna coordenadas reais do dispositivo.
                Cronograma XLSX requer arquivo de teste separado.
                Webhooks ERP apontam para o endpoint real.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
